import { useState, useRef, useMemo } from 'react';
import { Dialog, DialogHeader, DialogFooter } from './Dialog';
import { Button } from './Button';
import { formatPaise } from '../lib/cn';
import { QRCodeView } from './QRCodeView';
import {
  Printer,
  Copy,
  Check,
  TrendingDown,
  Calendar,
  MapPin,
  Sparkles,
} from 'lucide-react';
import { MDD_SUPPORT_PHONE_DISPLAY } from '../constants/contact';

export interface MandiRateSku {
  id?: number | null;
  code: string;
  name: string;
  category?: string | null;
  unit?: string | null;
  pricePaise: number;
  availableQty?: number | null;
  brand?: string | null;
  weightKg?: number | null;
}

export interface MandiRateCardModalProps {
  open: boolean;
  onClose: () => void;
  skus: MandiRateSku[];
  hubName?: string;
  portalUrl?: string;
  supportPhone?: string;
}

export function MandiRateCardModal({
  open,
  onClose,
  skus = [],
  hubName = 'Quality-Tested Central Hub, Bengaluru',
  portalUrl = 'https://mydailydirect.in',
  supportPhone = MDD_SUPPORT_PHONE_DISPLAY,
}: MandiRateCardModalProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');

  const todayDate = useMemo(() => {
    return new Date().toLocaleDateString('en-IN', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }, []);

  const categories = useMemo(() => {
    const set = new Set<string>();
    skus.forEach((s) => {
      if (s.category) set.add(s.category.trim());
    });
    return ['ALL', ...Array.from(set)];
  }, [skus]);

  const activeSkus = useMemo(() => {
    return skus.filter((s) => s.pricePaise > 0);
  }, [skus]);

  const visibleSkus = useMemo(() => {
    if (selectedCategory === 'ALL') return activeSkus;
    return activeSkus.filter((s) => s.category?.toLowerCase() === selectedCategory.toLowerCase());
  }, [activeSkus, selectedCategory]);

  const handlePrint = () => {
    window.print();
  };

  const handleCopyText = async () => {
    const lines = [
      `🌾 *MY DAILY DIRECT — TODAY'S QUALITY-TESTED WHOLESALE RATES*`,
      `📅 ${todayDate} | 📍 ${hubName}`,
      `⚡ Direct Mill-Gate Rates · Free Doorstep Kitchen Delivery`,
      `━━━━━━━━━━━━━━━━━━━━━━━━`,
    ];

    // Group by category
    const grouped = new Map<string, MandiRateSku[]>();
    for (const sku of activeSkus) {
      const cat = sku.category || 'General Wholesale';
      if (!grouped.has(cat)) grouped.set(cat, []);
      grouped.get(cat)!.push(sku);
    }

    grouped.forEach((items, cat) => {
      lines.push(`\n📦 *${cat.toUpperCase()}*`);
      items.slice(0, 8).forEach((item) => {
        const perKg = item.weightKg && item.weightKg > 0
          ? ` (₹${(item.pricePaise / 100 / item.weightKg).toFixed(1)}/kg)`
          : '';
        lines.push(`• ${item.name}: *${formatPaise(item.pricePaise)}*${perKg}`);
      });
    });

    lines.push(`\n━━━━━━━━━━━━━━━━━━━━━━━━`);
    lines.push(`🧾 100% GST Tax Invoices · Next-Morning Dispatch`);
    lines.push(`🛒 *View Full Live Catalog & Order Now:*`);
    lines.push(`👉 ${portalUrl}`);
    lines.push(`📞 *Call / WhatsApp Support:* ${supportPhone}`);

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
    }
  };

  return (
    <Dialog open={open} size="2xl" onClose={onClose}>
      <div className="flex flex-col flex-1 min-h-0 -m-4 sm:-m-6">
        {/* Modal Action Header */}
        <DialogHeader className="shrink-0 p-4 sm:p-5 border-b border-border bg-card pr-12 print:hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                Daily Wholesale Rate Card (QC Verified)
              </h3>
              <p className="text-xs text-muted-foreground">
                Export, print, or share today's mill rates to commercial buyers across WhatsApp & broadcast lists.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyText}
                className="gap-1.5 h-9 text-xs"
              >
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied to Clipboard!' : 'Copy WhatsApp Text'}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handlePrint}
                className="gap-1.5 h-9 text-xs"
              >
                <Printer className="h-4 w-4" /> Print / PDF Card
              </Button>
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pt-3 pb-1 hide-scrollbar">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  selectedCategory === cat
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {cat === 'ALL' ? 'All Commodities' : cat}
              </button>
            ))}
          </div>
        </DialogHeader>

        {/* Printable Rate Sheet Card Area */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 bg-slate-50 dark:bg-background/90 print:p-0 print:bg-white">
          <div
            ref={printRef}
            className="max-w-3xl mx-auto bg-white dark:bg-card border border-border rounded-2xl shadow-sm overflow-hidden print:border-none print:shadow-none print:rounded-none"
          >
            {/* Rate Card Header Banner */}
            <div className="bg-gradient-to-r from-amber-600 via-amber-700 to-amber-800 text-white p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black tracking-widest uppercase bg-white/20 px-2.5 py-0.5 rounded-full">
                      TRUSTED & QC-TESTED WHOLESALE
                    </span>
                    <span className="text-xs text-amber-200">|</span>
                    <span className="text-xs font-medium text-amber-100 flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" /> {hubName}
                    </span>
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
                    MY DAILY DIRECT
                  </h1>
                  <p className="text-xs sm:text-sm text-amber-100 font-medium">
                    Today’s Mill-Direct Wholesale Rates · ಇಂದಿನ ಸಗಟು ದರಗಳು
                  </p>
                </div>

                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-xs p-3 rounded-xl border border-white/20 self-start sm:self-auto">
                  <div className="bg-white p-1 rounded-lg">
                    <QRCodeView value={portalUrl} size={64} alt="Order Link QR" />
                  </div>
                  <div className="text-left text-xs">
                    <div className="font-bold text-white">Scan to Order</div>
                    <div className="text-[11px] text-amber-200">mydailydirect.in</div>
                    <div className="text-[10px] text-amber-100 mt-1 font-semibold">
                      {supportPhone}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-white/20 text-xs">
                <div className="flex items-center gap-1.5 font-semibold text-amber-100">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>Effective Date: <strong>{todayDate}</strong></span>
                </div>
                <div className="text-amber-200 text-[11px]">
                  ⚡ Valid for orders placed today by 9:00 PM for next-morning delivery
                </div>
              </div>
            </div>

            {/* Commodities Rate Table */}
            <div className="p-4 sm:p-6 space-y-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground border-b border-border pb-2">
                <span className="font-bold uppercase tracking-wider">
                  Live Commodity Listings ({visibleSkus.length} Items)
                </span>
                <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold">
                  <TrendingDown className="h-3.5 w-3.5" /> Mill Direct · Zero Hidden Margins
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground uppercase text-[10px] font-bold">
                      <th className="py-2.5 px-3">Commodity / Item</th>
                      <th className="py-2.5 px-2">Category</th>
                      <th className="py-2.5 px-2 text-right">Pack Size</th>
                      <th className="py-2.5 px-3 text-right">Wholesale Rate</th>
                      <th className="py-2.5 px-3 text-right">Rate / KG</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {visibleSkus.map((sku) => {
                      const weightKg = sku.weightKg && sku.weightKg > 0 ? sku.weightKg : 1;
                      const perKgPaise = Math.round(sku.pricePaise / weightKg);

                      return (
                        <tr key={sku.code} className="hover:bg-muted/40 transition-colors">
                          <td className="py-3 px-3">
                            <div className="font-bold text-foreground text-sm">{sku.name}</div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="font-mono text-[10px] text-muted-foreground">
                                {sku.code}
                              </span>
                              {sku.brand && (
                                <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300">
                                  {sku.brand}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-2 text-muted-foreground font-medium">
                            {sku.category || 'Staples'}
                          </td>
                          <td className="py-3 px-2 text-right font-semibold text-foreground">
                            {weightKg > 1 ? `${weightKg} KG` : sku.unit || '1 Unit'}
                          </td>
                          <td className="py-3 px-3 text-right font-extrabold text-foreground text-sm">
                            {formatPaise(sku.pricePaise)}
                          </td>
                          <td className="py-3 px-3 text-right font-bold text-primary">
                            {formatPaise(perKgPaise)}/kg
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Rate Card Footer Trust Highlights */}
            <div className="bg-muted/30 p-4 sm:p-5 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold">
                  ✓
                </div>
                <div>
                  <div className="font-bold text-foreground">100% Tax Invoices</div>
                  <div className="text-[11px]">Valid GST input credit on all orders</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-blue-500/10 text-blue-600 flex items-center justify-center font-bold">
                  🚚
                </div>
                <div>
                  <div className="font-bold text-foreground">Next-Morning Delivery</div>
                  <div className="text-[11px]">Dispatched 5 AM after QC Inspection</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center font-bold">
                  ₹
                </div>
                <div>
                  <div className="font-bold text-foreground">Doorstep Verification</div>
                  <div className="text-[11px]">Inspect weight & quality before pay</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <DialogFooter className="shrink-0 p-4 border-t border-border bg-card flex justify-between items-center print:hidden">
          <div className="text-xs text-muted-foreground">
            Displaying <strong>{visibleSkus.length}</strong> active wholesale prices.
          </div>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}
